import React from 'react';
import classNameBind from 'classnames/bind';
import get from 'lodash/get';
import moment from 'moment';
import { withTranslation } from '../Translator';
import Popover from '../Popover';
import Label from '../Label';
import styles from './ReportDateRangePicker.module.css';
import dateIsValid from './methods/dateIsValid';
import TimePeriod from './components/TimePeriod';
import {
  CUSTOM,
  DAY,
  WEEK,
  MONTH,
  QUARTER,
  TODAY,
  YESTERDAY,
  LAST7DAYS,
  LASTWEEK,
  LAST30DAYS,
  LASTMONTH,
  LASTQUARTER,
  LASTYEAR,
  THISMONTH, THISQUARTER, THISYEAR, THISWEEK,
} from './timePeriods';
import CalendarRangePickerNew from './components/CalendarRangePicker/CalendarRangePickerNew';
import isSameDay from './methods/isSameDay';
import getDateInAllowedRange from './methods/getDateInAllowedRange';
import { PickerButton } from '../PickerButton';

const FIRST_DAY_OF_THE_WEEK = 1;
const DATE_FORMAT = 'DD MMMM YYYY';
const bindClasses = classNameBind.bind(styles);

class ReportDateRangePicker extends React.Component {
  constructor(props) {
    super(props);

    const rawStartDate = get(props, 'value.startDate', null);
    const rawEndDate = get(props, 'value.endDate', null);
    const rawComparisonStartDate = get(props, 'value.comparisonStart', null);
    const rawComparisonEndDate = get(props, 'value.comparisonEnd', null);
    const startDate = this.getValidDate(rawStartDate);
    const endDate = this.getValidDate(rawEndDate);
    let comparisonStartDefault;
    let comparisonEndDefault;

    if (rawComparisonStartDate && rawComparisonEndDate) {
      comparisonStartDefault = rawComparisonStartDate;
      comparisonEndDefault = rawComparisonEndDate;
    } else {
      const {
        comparisonStart,
        comparisonEnd,
      } = this.calculateComparison(
        startDate,
        endDate,
        props.timePeriod,
        props.comparisonMode,
        props.comparisonEnabled,
      );
      comparisonStartDefault = comparisonStart;
      comparisonEndDefault = comparisonEnd;
    }

    this.state = {
      startDate,
      endDate,
      comparisonStart: comparisonStartDefault,
      comparisonEnd: comparisonEndDefault,
      comparisonEnabled: props.comparisonEnabled,
      comparisonMode: props.comparisonMode,
      timePeriod: props.timePeriod,
      selectionMode: 'normal',
      focusedInput: 0,
      activeId: props.timePeriod,
    };
    if (startDate !== rawStartDate || endDate !== rawEndDate) {
      this.handleRangeChange();
    }
    this.popoverRef = React.createRef();
  }

  componentDidMount() {
    if ([CUSTOM, DAY, WEEK, MONTH, QUARTER].indexOf(this.props.timePeriod) === -1) {
      this.checkAndEnforceDates(this.state.startDate, this.state.endDate);
    }
  }

  checkAndEnforceDates = (startDate, endDate) => {
    const { timePeriod, comparisonEnabled, comparisonMode, comparisonModeVisible, onChange } = this.props;
    let dates = this.handlePeriodChange(timePeriod);
    if (comparisonEnabled) {
      dates = { ...dates,
        ...this.calculateComparison(
          dates.startDate,
          dates.endDate,
          timePeriod,
          comparisonMode,
          comparisonEnabled,
        ) };
    }
    if (
      !moment(startDate).startOf('day').isSame(moment(dates.startDate).startOf('day'))
      || !moment(endDate).startOf('day').isSame(moment(dates.endDate).startOf('day'))
    ) {
      if (comparisonModeVisible) {
        onChange({
          ...dates,
          timePeriod,
        });
      } else {
        onChange({
          startDate: dates.startDate,
          endDate: dates.endDate,
          timePeriod,
        });
      }
      this.setState({
        ...dates,
      });
    }
  }

  onTimePeriodChange = (newPeriod) => {
    const { startDate, endDate } = this.handlePeriodChange(newPeriod.value);
    this.setState({
      timePeriod: newPeriod.value,
      startDate,
      endDate,
      focusedInput: 0,
      activeId: newPeriod.id,
    }, this.handleRangeChange);
  };

  onComparisonEnabledChange = () => {
    const { comparisonEnabled } = this.state;
    this.setState({
      comparisonEnabled: !comparisonEnabled,
    }, this.handleRangeChange);
  };

  onComparisonModeChange = (data) => {
    this.setState({
      comparisonMode: data,
    }, this.handleRangeChange);
  };

  getPickersLabel = () => {
    const {
      startDate,
      endDate,
      comparisonStart,
      comparisonEnd,
    } = this.state;
    const { t } = this.props;
    const labelGenerator = (start, end, isComparision) => {
      const locizeKey = `date.${isComparision ? 'day-month-year' : 'shorter'}`;
      if (isSameDay(start, end)) {
        return t(locizeKey, { date: start.toDate() });
      }
      if (dateIsValid(start) && dateIsValid(end)) {
        return `${t(locizeKey, { date: start.toDate() })} – ${t(locizeKey, { date: end.toDate() })}`;
      }

      return '';
    };
    const isComparision = Boolean(comparisonStart);
    const datePickerLabel = labelGenerator(startDate, endDate, isComparision) || t('selectDate');
    const comparisonLabel = labelGenerator(comparisonStart, comparisonEnd, isComparision);

    return (
      <React.Fragment>
        {datePickerLabel}
        {comparisonLabel && (
          <React.Fragment>
            <span className={bindClasses('vs')}> vs </span>
            {comparisonLabel}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  getValidDate = (date) => {
    const { min, max } = this.props;

    return getDateInAllowedRange(date, min, max);
  };

    calculateComparison = (startDate, endDate, timePeriod, comparisonMode, comparisonEnabled) => {
      if (comparisonEnabled && startDate && endDate) {
        let difference;
        const comparisonStart = startDate.clone();
        const comparisonEnd = endDate.clone();
        switch (comparisonMode) {
          case 'previous_time':
          case 'custom_range':
            switch (timePeriod) {
              case TODAY:
              case DAY:
              case YESTERDAY:
                comparisonStart.subtract(1, 'days');
                comparisonEnd.subtract(1, 'days');
                break;
              case LAST7DAYS:
              case LASTWEEK:
              case THISWEEK:
              case WEEK:
                comparisonStart.subtract(7, 'days');
                comparisonEnd.subtract(7, 'days');
                break;
              case LAST30DAYS:
                comparisonStart.subtract(30, 'days');
                comparisonEnd.subtract(30, 'days');
                break;
              case THISMONTH:
              case LASTMONTH:
              case MONTH:
                comparisonStart.subtract(1, 'month');
                comparisonEnd.subtract(1, 'month')
                  .endOf('month');
                break;
              case THISQUARTER:
              case LASTQUARTER:
              case QUARTER:
                comparisonStart.subtract(1, 'quarter');
                comparisonEnd.subtract(1, 'quarter')
                  .endOf('quarter');
                break;
              case THISYEAR:
              case LASTYEAR:
                comparisonStart.subtract(1, 'year');
                comparisonEnd.subtract(1, 'year')
                  .endOf('year');
                break;
              case CUSTOM:
                difference = comparisonEnd.diff(comparisonStart, 'days') + 1;
                comparisonStart.subtract(difference, 'days');
                comparisonEnd.subtract(difference, 'days');
                break;
              default:
                break;
            }
            break;
          case 'previous_year':
            comparisonStart.subtract(1, 'year');
            if (timePeriod === MONTH) {
              comparisonEnd.subtract(1, 'year').endOf('month');
            } else {
              comparisonEnd.subtract(1, 'year');
            }
            break;
          default:
            break;
        }

        return { comparisonStart, comparisonEnd };
      }

      return { comparisonStart: undefined, comparisonEnd: undefined };
    }

  handlePeriodChange = (period) => {
    const { startDate: startDateState, endDate: endDateState } = this.state;
    const { min, max } = this.props;
    let startDate = moment(startDateState);
    let endDate;
    switch (period) {
      case TODAY:
      case DAY:
        startDate = moment();
        endDate = moment();
        break;
      case YESTERDAY:
        startDate = moment().subtract(1, 'day');
        endDate = moment().subtract(1, 'day');
        break;
      case LAST7DAYS:
        startDate = moment().subtract(7, 'days');
        endDate = moment().subtract(1, 'day');
        break;
      case WEEK:
      case LASTWEEK:
        startDate = moment().startOf('isoWeek').subtract(7, 'days');
        endDate = moment().startOf('isoWeek').subtract(1, 'day');
        break;
      case LAST30DAYS:
        startDate = moment().subtract(30, 'days');
        endDate = moment().subtract(1, 'day');
        break;
      case THISMONTH:
        startDate = moment().startOf('month');
        endDate = moment().endOf('month');
        break;
      case MONTH:
      case LASTMONTH:
        startDate = moment().startOf('month').subtract(1, 'month');
        endDate = moment().startOf('month').subtract(1, 'day');
        break;
      case THISQUARTER:
        startDate = moment().startOf('quarter');
        endDate = moment().endOf('quarter');
        break;
      case THISWEEK:
        startDate = moment().startOf('isoWeek');
        endDate = moment().endOf('isoWeek');
        break;
      case QUARTER:
      case LASTQUARTER:
        startDate = moment().startOf('quarter').subtract(1, 'quarter');
        endDate = moment().startOf('quarter').subtract(1, 'day');
        break;
      case THISYEAR:
        startDate = moment().startOf('year');
        endDate = moment().endOf('year');
        break;
      case LASTYEAR:
        startDate = moment().startOf('year').subtract(1, 'year');
        endDate = moment().startOf('year').subtract(1, 'day');
        break;
      case CUSTOM:
        startDate = moment(startDate);
        endDate = moment(endDateState);
        break;
      default:
        break;
    }

    return {
      startDate: getDateInAllowedRange(startDate, min, max),
      endDate: getDateInAllowedRange(endDate, min, max),
    };
  }

  handleRangeChange = () => {
    const { comparisonModeVisible } = this.props;
    const {
      startDate,
      endDate,
      comparisonEnabled,
      comparisonMode,
      timePeriod,
    } = this.state;
    if (comparisonModeVisible) {
      const {
        comparisonStart,
        comparisonEnd,
      } = this.calculateComparison(
        startDate,
        endDate,
        timePeriod,
        comparisonMode,
        comparisonEnabled,
      );

      this.setState({
        comparisonStart,
        comparisonEnd,
        focusedInput: 0,
      });
    }
  };

    submitChanges = (callback) => {
      const { onChange, comparisonModeVisible } = this.props;
      const {
        startDate,
        endDate,
        comparisonEnabled,
        comparisonMode,
        comparisonStart,
        comparisonEnd,
        timePeriod,
      } = this.state;
      if (comparisonModeVisible) {
        onChange({
          startDate,
          endDate,
          comparisonStart,
          comparisonEnd,
          comparisonEnabled,
          comparisonMode,
          timePeriod,
        });
      } else {
        onChange({
          startDate,
          endDate,
          timePeriod,
        });
      }
      this.setState({
        blockReset: true,
      }, callback);
    }

  handleChange = (startDate, endDate, period = CUSTOM) => {
    this.setState(
      {
        startDate: this.getValidDate(startDate),
        endDate: this.getValidDate(endDate),
        timePeriod: period,
        focusedInput: 0,
      },
      this.handleRangeChange,
    );
  };

    resetAll = () => {
      const rawStartDate = get(this.props, 'value.startDate', null);
      const rawEndDate = get(this.props, 'value.endDate', null);
      const rawComparisonStartDate = get(this.props, 'value.comparisonStart', null);
      const rawComparisonEndDate = get(this.props, 'value.comparisonEnd', null);
      const startDate = this.getValidDate(rawStartDate);
      const endDate = this.getValidDate(rawEndDate);
      let comparisonStartDefault;
      let comparisonEndDefault;
      const { comparisonEnabled, comparisonMode, timePeriod } = this.props;
      if (this.state.blockReset) {
        this.setState({
          blockReset: false,
        });
      } else {
        if (rawComparisonStartDate && rawComparisonEndDate) {
          comparisonStartDefault = rawComparisonStartDate;
          comparisonEndDefault = rawComparisonEndDate;
        } else {
          const {
            comparisonStart,
            comparisonEnd,
          } = this.calculateComparison(
            startDate,
            endDate,
            timePeriod,
            comparisonMode,
            comparisonEnabled,
          );
          comparisonStartDefault = comparisonStart;
          comparisonEndDefault = comparisonEnd;
        }

        this.setState({
          startDate,
          endDate,
          comparisonStart: comparisonStartDefault,
          comparisonEnd: comparisonEndDefault,
          comparisonEnabled,
          comparisonMode,
          timePeriod,
          selectionMode: 'normal',
          focusedInput: 0,
          activeId: timePeriod,
        });
      }
    }

  handleComparisonChangeCalendar = (newComparisonStart, newComparisonEnd) => {
    const {
      comparisonStart: oldComparisonStart,
      comparisonEnd: oldComparisonEnd,
      timePeriod,
      startDate,
      endDate,
    } = this.state;
    const newComparisonStartTemp = newComparisonStart || oldComparisonStart;
    const newComparisonEndTemp = newComparisonEnd || oldComparisonEnd;

    let difference = 0;
    let comparisonStart = moment(newComparisonStartTemp);
    let comparisonEnd = moment(newComparisonEndTemp);
    switch (timePeriod) {
      case DAY:
        comparisonEnd = moment(newComparisonStartTemp);
        comparisonStart = moment(newComparisonStartTemp);
        break;
      case WEEK:
        comparisonStart = moment(newComparisonStartTemp).startOf('isoWeek');
        comparisonEnd = moment(newComparisonStartTemp).endOf('isoWeek');
        break;
      case MONTH:
        comparisonStart = moment(newComparisonStartTemp).startOf('month');
        comparisonEnd = moment(newComparisonStartTemp).endOf('month');
        break;
      case CUSTOM:
        difference = endDate.diff(startDate, 'days');
        comparisonStart = moment(newComparisonStartTemp);
        comparisonEnd = moment(newComparisonStartTemp).add(difference, 'days');
        break;
      default:
        break;
    }

    this.setState(
      {
        comparisonStart,
        comparisonEnd,
        focusedInput: 0,
      },
    );
  }

  handleComparisonChange = (newComparisonStart, newComparisonEnd) => {
    const {
      comparisonStart: oldComparisonStart,
      comparisonEnd: oldComparisonEnd,
      timePeriod,
      startDate,
      endDate,
    } = this.state;
    const newComparisonStartTemp = newComparisonStart || oldComparisonStart;
    const newComparisonEndTemp = newComparisonEnd || oldComparisonEnd;

    let difference = 0;
    let comparisonStart = moment(newComparisonStartTemp);
    let comparisonEnd = moment(newComparisonEndTemp);
    const firstDateChanged = (
      newComparisonStartTemp.format(DATE_FORMAT) !== oldComparisonStart.format(DATE_FORMAT)
    );
    const secondDateChanged = (
      newComparisonEndTemp.format(DATE_FORMAT) !== oldComparisonEnd.format(DATE_FORMAT)
    );

    switch (timePeriod) {
      case DAY:
        if (firstDateChanged) {
          comparisonEnd = moment(newComparisonStartTemp);
          comparisonStart = moment(newComparisonStartTemp);
        }
        if (secondDateChanged) {
          comparisonStart = moment(newComparisonEndTemp);
          comparisonEnd = moment(newComparisonEndTemp);
        }
        break;
      case WEEK:
        if (firstDateChanged) {
          comparisonStart = moment(newComparisonStartTemp).startOf('isoWeek');
          comparisonEnd = moment(newComparisonStartTemp).endOf('isoWeek');
        }
        if (secondDateChanged) {
          comparisonStart = moment(newComparisonEndTemp).startOf('isoWeek');
          comparisonEnd = moment(newComparisonEndTemp).endOf('isoWeek');
        }
        break;
      case MONTH:
        if (firstDateChanged) {
          comparisonStart = moment(newComparisonStartTemp).startOf('month');
          comparisonEnd = moment(newComparisonStartTemp).endOf('month');
        }
        if (secondDateChanged) {
          comparisonStart = moment(newComparisonEndTemp).startOf('month');
          comparisonEnd = moment(newComparisonEndTemp).endOf('month');
        }
        break;
      case CUSTOM:
        difference = endDate.diff(startDate, 'days');
        if (firstDateChanged) {
          comparisonStart = moment(newComparisonStartTemp);
          comparisonEnd = moment(newComparisonStartTemp).add(difference, 'days');
        }
        if (secondDateChanged) {
          comparisonStart = moment(newComparisonEndTemp).subtract(difference, 'days');
          comparisonEnd = moment(newComparisonEndTemp);
        }
        break;
      default:
        break;
    }
    if (firstDateChanged || secondDateChanged) {
      this.setState(
        {
          comparisonStart,
          comparisonEnd,
          focusedInput: 0,
        },
      );
    }
  }

  render() {
    const {
      timePeriod,
      startDate,
      endDate,
      comparisonMode,
      comparisonEnabled,
      comparisonStart,
      comparisonEnd,
      focusedInput,
    } = this.state;
    const {
      label,
      min,
      max,
      comparisonModeVisible,
      position,
      dataId,
      i18n,
      onOpen,
      onClose,
      autoSubmit,
      disabled,
      isDemoSite,
    } = this.props;

    const value = this.getPickersLabel();

    return (
      <div className={bindClasses('wrapper')} data-ppuc-id={dataId}>
        {label ? <Label type="small">{label}</Label> : null}
        <Popover
          activatorElement={(
            <PickerButton
              icon={{ type: 'icon', content: 'calendar' }}
              dataId={dataId}
              disabled={disabled}
            >
              {value}
            </PickerButton>
          )}
          subdued
          speechBubble
          position={position}
          onOpen={onOpen}
          onClose={onClose}
          ref={this.popoverRef}
          dataId="ReportDateRangePicker"
          disabled={disabled}
        >
          <div className={bindClasses('popover-layout')}>
            <TimePeriod
              isDemoSite={isDemoSite}
              period={timePeriod}
              onComparisonEnabledChange={this.onComparisonEnabledChange}
              onComparisonModeChange={this.onComparisonModeChange}
              onTimePeriodChange={this.onTimePeriodChange}
              onDateRangeChange={this.handleChange}
              onComparisonRangeChange={this.handleComparisonChange}
              {...{
                startDate,
                endDate,
                comparisonEnabled,
                comparisonMode,
                comparisonModeVisible,
                comparisonStart,
                comparisonEnd,
                min,
                max,
              }}
              dataId={`${dataId}.TimePicker`}
              changeMode={(mode) => { this.setState({ selectionMode: mode }); }}
              toggleFocus={(id) => { this.setState({ focusedInput: id }); }}
              focusedInput={focusedInput}
              activeId={this.state.activeId}
              popoverRef={this.popoverRef}
              onApply={this.submitChanges}
              autoSubmit={autoSubmit}
            >
              <div className={bindClasses('calendar')}>
                <CalendarRangePickerNew
                  from={startDate}
                  to={endDate}
                  min={min}
                  max={max}
                  period={timePeriod}
                  firstDayOfWeek={FIRST_DAY_OF_THE_WEEK}
                  onChange={this.state.selectionMode === 'normal' ? this.handleChange : this.handleComparisonChangeCalendar}
                  language={i18n.language}
                  comparisonStart={comparisonStart}
                  comparisonEnd={comparisonEnd}
                  comparisonEnabled={comparisonEnabled}
                  selectionMode={this.state.selectionMode}
                  comparisonMode={comparisonMode}
                  onClose={autoSubmit ? this.submitChanges : this.resetAll}
                  forceChangeTimePeriod={(val) => {
                    this.onTimePeriodChange(val);
                    this.setState({
                      activeId: 'custom',
                    });
                  }}
                />
              </div>
            </TimePeriod>
          </div>
        </Popover>
      </div>
    );
  }
}

ReportDateRangePicker.defaultProps = {
  value: null,
  label: '',
  /* eslint-disable-next-line max-len */
  // Default values are based on ClickHouse date support used in Analytics (PPNA-361) - updated by comparison
  // 1971-01-01
  min: moment(31532400000),
  // 2104-12-31
  max: moment(4260121200000),
  comparisonEnabled: false,
  comparisonMode: 'previous_time',
  comparisonModeVisible: false,
  position: 'bottom-end',
  timePeriod: CUSTOM,
  dataId: 'ReportDateRangePicker',
  onOpen: () => {},
  onClose: () => {},
  autoSubmit: false,
  isDemoSite: false,
};

const enhanced = withTranslation('components')(ReportDateRangePicker);
enhanced.displayName = 'ReportDateRangePicker';

export default enhanced;
export const PureComponent = ReportDateRangePicker;
